import { withSessionDetailPairs } from "./session-metadata";
import type {
  StepCaptureData,
  StepDetailsData,
} from "contexts/MultiStepCaptureContext";
import {
  getOrbDetailPairs,
  getOrbStepLabelDetails,
  OrbDetailValue,
} from "lib/orb-qr";
import { getStepDropdownValues } from "lib/label-relations";
import {
  getOrbStepDetailFields,
  ResolvedWorkTask,
  ResolvedWorkTaskStep,
} from "lib/work-tasks";
import type { PrismicCollectionDetailField } from "types";

export const RTMS_RUN_STEP = -1;
export const RTMS_QR_PREFIX = "rtms-sequence:";
export const RTMS_QR_MAX_BYTES = 1200;

// An internal content revision, not an authentication or security token.
function contentRevision(value: string): string {
  return [2166136261, 3339675911]
    .map((seed) => {
      let hash = seed;
      for (let index = 0; index < value.length; index++) {
        hash = Math.imul(hash ^ value.charCodeAt(index), 16777619);
      }
      return (hash >>> 0).toString(16).padStart(8, "0");
    })
    .join("");
}

export interface RtmsRun {
  id: string;
  identity: string;
  plan: string;
}

export function getRtmsRun(details: StepDetailsData[]): RtmsRun | undefined {
  return details.find((item) => item.stepNumber === RTMS_RUN_STEP)?.rtmsRun;
}

export function getRtmsRunPlan(flow: ResolvedWorkTask): string {
  return JSON.stringify(
    flow.steps.map((step) => ({
      number: step.stepNumber,
      task: step.captureTaskId,
      type: step.captureType,
      sequence: step.rtmsPlanFingerprint || "",
    }))
  );
}

/** Start at the selected step, stopping before another sequence, modality, or context form. */
export function getRtmsSection(
  flow: ResolvedWorkTask,
  first: ResolvedWorkTaskStep
) {
  const start = flow.steps.indexOf(first);
  if (start < 0 || !first.rtmsSequence) return [];
  const section: ResolvedWorkTaskStep[] = [];
  for (const step of flow.steps.slice(start)) {
    if (
      step.captureType !== "rtms" ||
      step.rtmsSequence?.id !== first.rtmsSequence.id
    )
      break;
    // Even optional configured input gets its own form; never silently omit it.
    if (
      section.length &&
      (step.rtmsContextEntry === "ask_before_task" ||
        getOrbStepDetailFields(step).length)
    )
      break;
    section.push(step);
  }
  return section;
}

/** Navigation units: one QR section for grouped RTMS, one screen per other step. */
export function getWorkTaskSections(flow: ResolvedWorkTask) {
  const sections: ResolvedWorkTaskStep[][] = [];
  for (let index = 0; index < flow.steps.length; ) {
    const step = flow.steps[index];
    const section = step.rtmsSequence ? getRtmsSection(flow, step) : [step];
    sections.push(section);
    index += section.length;
  }
  return sections;
}

export function getRtmsSectionStart(
  flow: ResolvedWorkTask,
  step: ResolvedWorkTaskStep
): ResolvedWorkTaskStep {
  if (!step.rtmsSequence) return step;
  return (
    getWorkTaskSections(flow).find((section) =>
      section.some((item) => item.stepNumber === step.stepNumber)
    )?.[0] || step
  );
}

export function getRtmsFieldValues(
  fields: PrismicCollectionDetailField[],
  details: StepDetailsData[],
  stepNumber: number
): OrbDetailValue[] {
  const saved =
    details.find((item) => item.stepNumber === stepNumber)?.stepDetailsFields ||
    [];
  return fields.flatMap((field) => {
    const id = String(field.label_id || "").trim();
    const value = saved.find((item) => item.label_id === id)?.value?.trim();
    return value ? [{ label_id: id, value }] : [];
  });
}

export function validateRtmsValues(
  fields: PrismicCollectionDetailField[],
  values: OrbDetailValue[]
): string[] {
  return fields.flatMap((field) => {
    const id = String(field.label_id || "").trim();
    const value =
      values.find((item) => item.label_id === id)?.value?.trim() || "";
    if (!value)
      return field.is_required ? [String(field.label_title || id)] : [];
    if (
      field.label_type === "dropdown" &&
      !getStepDropdownValues(field).includes(value)
    )
      return [String(field.label_title || id)];
    if (field.label_type === "numeric" && !Number.isFinite(Number(value)))
      return [String(field.label_title || id)];
    if (
      field.label_type === "date" &&
      (!/^\d{4}-\d{2}-\d{2}$/.test(value) ||
        Number.isNaN(Date.parse(value)) ||
        new Date(value).toISOString().slice(0, 10) !== value)
    ) {
      return [String(field.label_title || id)];
    }
    return [];
  });
}

export function getRtmsSequenceValues(
  step: ResolvedWorkTaskStep,
  details: StepDetailsData[]
): OrbDetailValue[] {
  const sequence = step.rtmsSequence;
  if (!sequence) return [];
  const saved = details.find(
    (item) => item.stepNumber === sequence.firstStepNumber
  )?.rtmsSequenceDetails;
  if (saved?.sequenceId !== sequence.id) return [];
  return sequence.fields.flatMap((field) => {
    const id = String(field.label_id || "").trim();
    const value = saved.fields
      .find((item) => item.label_id === id)
      ?.value?.trim();
    return value ? [{ label_id: id, value }] : [];
  });
}

function mergeRtmsDetails(values: OrbDetailValue[]): OrbDetailValue[] {
  const merged = new Map<string, string>();
  for (const { label_id, value } of values) {
    if (merged.has(label_id) && merged.get(label_id) !== value) {
      throw new Error(
        `Conflicting values for label ${label_id}. Please check the task configuration.`
      );
    }
    merged.set(label_id, value);
  }
  return Array.from(merged, ([label_id, value]) => ({ label_id, value }));
}

function getRtmsSharedDetails(
  step: ResolvedWorkTaskStep,
  details: StepDetailsData[],
  globalDetails: unknown
): OrbDetailValue[] {
  const globalValues = getOrbDetailPairs(globalDetails).map((pair) => {
    const index = pair.indexOf(",");
    return { label_id: pair.slice(0, index), value: pair.slice(index + 1) };
  });
  return mergeRtmsDetails([
    ...globalValues,
    ...getRtmsSequenceValues(step, details),
  ]);
}

export function getRtmsScopedDetails(
  step: ResolvedWorkTaskStep,
  details: StepDetailsData[],
  globalDetails: unknown
): OrbDetailValue[] {
  const sequence = step.rtmsSequence;
  if (!sequence) throw new Error("RTMS sequence is missing");
  const taskValues = getRtmsFieldValues(
    getOrbStepDetailFields(step),
    details,
    step.stepNumber
  );
  return mergeRtmsDetails([
    ...getRtmsSharedDetails(step, details, globalDetails),
    ...taskValues,
    ...getOrbStepLabelDetails(step.stepLabel),
  ]);
}

export function getRtmsStepFingerprint(
  step: ResolvedWorkTaskStep,
  details: StepDetailsData[],
  shared: unknown,
  attempt: number
): string {
  const run = getRtmsRun(details);
  return JSON.stringify({
    version: 1,
    runId: run?.id,
    plan: step.rtmsPlanFingerprint,
    step: step.stepNumber,
    attempt,
    details: getRtmsScopedDetails(step, details, shared),
  });
}

export interface RtmsSequencePayload {
  version: 2;
  user_id: string;
  session_id: string;
  collection_id: string;
  work_task_id: string;
  attempt: number;
  // Work Task and sequence labels, applied to every entry in this section.
  collection_details: string[];
  tasks: Array<{
    task_id: string;
    task_step_number: number;
    work_step_number: number;
    number_of_captures: number;
    // Task/step labels only; combine with the payload-level collection_details.
    collection_details: string[];
  }>;
}

export function buildRtmsSequenceQr({
  flow,
  section,
  run,
  userId,
  sessionId,
  collectionId,
  details,
  shared,
  attempt,
  sessionMetadata = {},
}: {
  flow: ResolvedWorkTask;
  section: ResolvedWorkTaskStep[];
  run: RtmsRun;
  userId: string;
  sessionId: string;
  collectionId: string;
  details: StepDetailsData[];
  shared: unknown;
  sessionMetadata?: Record<string, string>;
  attempt: number;
}) {
  if (
    !section.length ||
    !section[0].rtmsSequence ||
    !run.id ||
    !userId ||
    !sessionId ||
    !collectionId
  ) {
    throw new Error("The sequence QR is missing required identifiers");
  }
  const first = section[0];
  const sequence = first.rtmsSequence!;
  if (run.plan !== getRtmsRunPlan(flow))
    throw new Error(
      "The task configuration changed. Please reopen the Work Task."
    );
  const globalIds = new Set(
    getOrbDetailPairs(shared)
      .filter((pair) => pair.slice(pair.indexOf(",") + 1).trim())
      .map((pair) => pair.slice(0, pair.indexOf(",")))
  );
  const missingGlobal = flow.sharedCollectionDetailFields.filter(
    (field) =>
      field.is_required && !globalIds.has(String(field.label_id || "").trim())
  );
  if (missingGlobal.length)
    throw new Error("Please complete the required Work Task details first.");
  const invalid = validateRtmsValues(
    sequence.fields,
    getRtmsSequenceValues(first, details)
  );
  section.forEach((step) =>
    invalid.push(
      ...validateRtmsValues(
        getOrbStepDetailFields(step),
        getRtmsFieldValues(
          getOrbStepDetailFields(step),
          details,
          step.stepNumber
        )
      )
    )
  );
  if (invalid.length)
    throw new Error(
      `Please complete or correct: ${Array.from(new Set(invalid)).join(", ")}`
    );
  const sharedDetails = withSessionDetailPairs(
    getRtmsSharedDetails(first, details, shared).map(
      ({ label_id, value }) => `${label_id},${value}`
    ),
    sessionMetadata
  );
  const sharedLabelIds = new Set(
    sharedDetails.map((pair) => pair.slice(0, pair.indexOf(",")))
  );
  const payload: RtmsSequencePayload = {
    version: 2,
    user_id: userId.startsWith("userid:") ? userId : `userid:${userId}:1`,
    session_id: sessionId,
    collection_id: collectionId,
    work_task_id: flow.workTaskId,
    attempt,
    collection_details: sharedDetails.map((pair) => {
      const index = pair.indexOf(",");
      return `${pair.slice(0, index)}: ${pair.slice(index + 1)}`;
    }),
    tasks: section.map((step) => ({
      task_id: step.captureTaskId,
      task_step_number: step.captureTaskStepNumber,
      work_step_number: step.stepNumber,
      number_of_captures: step.numberOfCaptures,
      // Validate all scopes before dropping identical shared labels.
      collection_details: getRtmsScopedDetails(step, details, shared)
        .filter(({ label_id }) => !sharedLabelIds.has(label_id))
        .map(({ label_id, value }) => `${label_id}: ${value}`),
    })),
  };
  // Keep progress/retry identity local; the collector only needs capture metadata.
  const launchId = `${run.id}:${sequence.id}:${
    first.stepNumber
  }:${attempt}:${contentRevision(
    JSON.stringify({
      version: payload.version,
      collection_details: payload.collection_details,
      tasks: payload.tasks,
    })
  )}`;
  const content = RTMS_QR_PREFIX + JSON.stringify(payload);
  // Blob is UTF-8 aware, including non-ASCII labels and participant values.
  if (new Blob([content]).size > RTMS_QR_MAX_BYTES) {
    throw new Error(
      "This section is too large for a scannable QR. Add a participant-input pause to split it, or shorten its labels."
    );
  }
  return { payload, content, launchId };
}

export function getRtmsAttempt(
  section: ResolvedWorkTaskStep[],
  captures: StepCaptureData[],
  redo: boolean
): number {
  return (
    Math.max(
      0,
      ...section.map(
        (step) =>
          captures.find((capture) => capture.stepNumber === step.stepNumber)
            ?.rtmsConfirmation?.attempt || 0
      )
    ) + (redo ? 1 : 0)
  );
}
